import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAddressSuggestions,
  fetchResolvedAddress,
  type AddressSuggestion,
  type ResolvedAddress,
} from "../../services/address/googlePlaces";

interface AddressAutocompleteInputProps {
  name?: string;
  value: string;
  onChange: (nextValue: string) => void;
  onResolvedAddress?: (address: ResolvedAddress) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  regionCodes?: string[];
  languageCode?: string;
}

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 3;
const readEnvString = (key: string): string => {
  const env = import.meta.env as Record<string, unknown>;
  const value = env[key];
  return typeof value === "string" ? value.trim() : "";
};

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  name,
  value,
  onChange,
  onResolvedAddress,
  className = "",
  inputClassName = "",
  placeholder = "Start typing an address",
  required = false,
  disabled = false,
  regionCodes,
  languageCode = "en",
}) => {
  const apiKey = readEnvString("VITE_GOOGLE_MAPS_API_KEY");
  const autocompleteEnabled = Boolean(apiKey);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const requestCounterRef = useRef(0);
  const blurTimeoutRef = useRef<number | null>(null);

  const normalizedRegionCodes = useMemo(
    () =>
      Array.isArray(regionCodes)
        ? regionCodes
            .map((code) => code.trim().toLowerCase())
            .filter((code) => code.length === 2)
        : undefined,
    [regionCodes],
  );

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!autocompleteEnabled || disabled || !isOpen) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const query = value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    const currentRequestId = requestCounterRef.current + 1;
    requestCounterRef.current = currentRequestId;

    const timer = window.setTimeout(() => {
      setIsLoading(true);
      setErrorMessage("");
      void (async () => {
        try {
          const nextSuggestions = await fetchAddressSuggestions(query, apiKey, {
            regionCodes: normalizedRegionCodes,
            languageCode,
          });

          if (requestCounterRef.current === currentRequestId) {
            setSuggestions(nextSuggestions);
          }
        } catch (error) {
          if (requestCounterRef.current === currentRequestId) {
            setSuggestions([]);
            setErrorMessage(
              error instanceof Error ? error.message : "Address lookup failed",
            );
          }
        } finally {
          if (requestCounterRef.current === currentRequestId) {
            setIsLoading(false);
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    apiKey,
    autocompleteEnabled,
    disabled,
    isOpen,
    languageCode,
    normalizedRegionCodes,
    value,
  ]);

  const handleSuggestionSelect = (suggestion: AddressSuggestion): void => {
    onChange(suggestion.primaryText);
    setIsOpen(false);
    setSuggestions([]);
    setErrorMessage("");

    if (!onResolvedAddress || !autocompleteEnabled) {
      return;
    }

    setIsLoading(true);
    void (async () => {
      try {
        const resolvedAddress = await fetchResolvedAddress(
          suggestion.placeId,
          apiKey,
        );
        onResolvedAddress(resolvedAddress);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Address lookup failed",
        );
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        name={name}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          if (autocompleteEnabled && !disabled) {
            setIsOpen(true);
          }
        }}
        onFocus={() => {
          if (!disabled && autocompleteEnabled) {
            setIsOpen(true);
          }
        }}
        onBlur={() => {
          blurTimeoutRef.current = window.setTimeout(() => {
            setIsOpen(false);
          }, 120);
        }}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
      />

      {autocompleteEnabled && isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
              Searching addresses...
            </div>
          ) : suggestions.length > 0 ? (
            <ul className="max-h-56 overflow-auto py-1">
              {suggestions.map((suggestion) => (
                <li key={suggestion.placeId}>
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => {
                      handleSuggestionSelect(suggestion);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-red-50 focus:bg-red-50 focus:outline-none dark:text-gray-100 dark:hover:bg-red-900/20 dark:focus:bg-red-900/20"
                  >
                    <div className="font-medium">{suggestion.primaryText}</div>
                    {suggestion.secondaryText ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {suggestion.secondaryText}
                      </div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : value.trim().length >= MIN_QUERY_LENGTH ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              No matches found
            </div>
          ) : null}
        </div>
      )}

      {errorMessage ? (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
};

export default AddressAutocompleteInput;
